"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Pipette, Layers, Clock, FileCheck, ChevronDown, MessageSquare, ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  iconName: "ShieldCheck",
  title: "How do you ensure zero water intrusion in walk-in showers?",
  content: "Every shower build uses a continuous Schluter-Kerdi bonded waterproofing membrane across all walls, benches, and recessed niches. Before laying any tile, we complete a mandatory 24-hour standing water flood test to verify watertight integrity on both curbed and flush curbless transitions."
}, {
  id: "item-2",
  iconName: "Pipette",
  title: "Can existing plumbing stacks be moved in older Decatur homes?",
  content: "Yes. Many historic Decatur bungalows and mid-century homes have cast-iron or undersized venting. Our team sister-joists subfloors, reinforces load paths, and routes updated PVC or cast drain lines so you can relocate vanities, free-standing soaking tubs, and wet rooms without structural compromise."
}, {
  id: "item-3",
  iconName: "Layers",
  title: "What tile and surface materials perform best in humid master baths?",
  content: "We guide you toward dense, non-porous engineered quartz vanity tops, slip-rated honed porcelain or unglazed mosaic shower floors, and factory-sealed natural marble. All custom vanities receive moisture-resistant conversion varnishes designed specifically for high-steam environments."
}, {
  id: "item-4",
  iconName: "Clock",
  title: "What is the typical timeline from demolition to final punch list?",
  content: "A comprehensive master bathroom remodel generally takes 4 to 8 weeks of active on-site work. This schedule accounts for systematic phases: demolition and structural adjustments (week 1), rough plumbing and electrical inspections (week 2), full waterproofing and inspection (week 3), precision tile setting (weeks 4-5), and fixture trim-out with custom glass installation (weeks 6-8)."
}, {
  id: "item-5",
  iconName: "FileCheck",
  title: "Who manages City of Decatur permits and trade inspections?",
  content: "Decatur Heritage Renovations manages all plan submissions, building permits, and trade inspections with the City of Decatur and DeKalb County. Our trade specialists coordinate all rough-in and final plumbing and electrical life-safety sign-offs directly."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const [openItem, setOpenItem] = useState<string | null>("item-1");
  const toggleItem = (id: string) => {
    setOpenItem(prev => prev === id ? null : id);
  };
  const getFaqIcon = (name: string) => {
    switch (name) {
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-chart-1" />;
      case "Pipette":
        return <Pipette className="size-5 text-chart-1" />;
      case "Layers":
        return <Layers className="size-5 text-chart-1" />;
      case "Clock":
        return <Clock className="size-5 text-chart-1" />;
      case "FileCheck":
        return <FileCheck className="size-5 text-chart-1" />;
      default:
        return <ShieldCheck className="size-5 text-chart-1" />;
    }
  };
  return <section id="bathroom-remodeling-faq" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col items-center">
            <Badge variant="outline" className="mb-4 border-border bg-secondary px-3 py-1 font-sans text-xs font-semibold tracking-wider text-secondary-foreground uppercase">
              Clarity &amp; Standards
            </Badge>

            <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Bathroom remodeling insights and standards
            </h2>

            {/* Architectural Copper Hairline Accent */}
            <div className="mt-4 mb-5 h-0.5 w-16 bg-chart-1" />

            <p className="font-sans text-base text-muted-foreground sm:text-lg">
              Technical waterproofing methods, City of Decatur building codes,
              and material selections explained for local homeowners.
            </p>
          </motion.div>
        </div>

        {/* Accordion Container */}
        <div className="mx-auto mt-12 max-w-3xl sm:mt-16">
          <div className="flex flex-col gap-3.5">
            {faqItems.map((item, i) => {
            const isOpen = openItem === item.id;
            return <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: shouldReduceMotion ? 0 : i * 0.08,
              ease: [0.16, 1, 0.3, 1]
            }} className="overflow-hidden rounded-lg border border-border bg-card shadow-2xs transition-[border-color,box-shadow] duration-300 hover:border-border/90 hover:shadow-xs">
                  <Button type="button" variant="ghost" onClick={() => toggleItem(item.id)} aria-expanded={isOpen} aria-controls={`faq-answer-${item.id}`} className="flex h-auto w-full items-center justify-between gap-4 px-5 py-4.5 text-left hover:bg-transparent transition-colors sm:px-6 sm:py-5">
                    <div className="flex items-center gap-3.5 sm:gap-4">
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-secondary">
                        {getFaqIcon(item.iconName)}
                      </div>
                      <h3 className="font-sans text-base font-semibold text-foreground sm:text-lg">
                        {item.title}
                      </h3>
                    </div>

                    <div className={`flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground transition-transform duration-300 ${isOpen ? "rotate-180 text-foreground" : ""}`}>
                      <ChevronDown className="size-4" />
                    </div>
                  </Button>

                  <div id={`faq-answer-${item.id}`} role="region" className={`grid transition-[grid-template-rows,opacity] duration-350 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                    <div className="overflow-hidden">
                      <div className="border-t border-border/60 px-5 pt-3.5 pb-5 text-sm leading-relaxed text-muted-foreground sm:px-6 sm:pt-4 sm:pb-6 sm:text-base">
                        <p>{item.content}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Bottom Reassurance Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto mt-12 max-w-3xl sm:mt-16">
          <div className="rounded-xl border border-border bg-secondary p-6 sm:p-8">
            <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
              <div className="flex items-start gap-4">
                <div className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg bg-card shadow-2xs">
                  <MessageSquare className="size-5 text-chart-1" />
                </div>
                <div>
                  <h4 className="font-sans text-base font-semibold text-foreground sm:text-lg">
                    Have a custom bathroom layout or historic floor plan?
                  </h4>
                  <p className="mt-1 font-sans text-sm text-muted-foreground">
                    Discuss structural load adjustments, plumbing moves, and finish options with our lead builders.
                  </p>
                </div>
              </div>

              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:shrink-0">
                <Button asChild variant="outline" size="default" className="w-full border-border bg-card text-foreground hover:bg-muted sm:w-auto">
                  <Link to="#" className="inline-flex items-center justify-center gap-2">
                    Ask a Specialist
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="default" className="w-full border-border bg-card text-foreground hover:bg-muted sm:w-auto">
                  <a href="tel:+14708479076" className="inline-flex items-center justify-center gap-2">
                    <Phone className="size-4 text-chart-1" />
                    (470) 847-9076
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}